import React, { useEffect, useState } from "react";
import { Card, Button, Spinner, Alert, Row, Col } from "react-bootstrap";
import { GoCheckCircleFill } from "react-icons/go";
import { useNavigate, useLocation } from "react-router-dom";

function LearnerAssessment() {
  const navigate = useNavigate();
  const location = useLocation();
  const moduleId = location.state?.moduleId || 1;

  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.user_id) {
          setUserId(parsedUser.user_id);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchAssessments = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${process.env.REACT_APP_API}/getLearnerAssessment?moduleId=${moduleId}&userId=${userId}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch assessments");
        }

        const data = await response.json();

        if (data.success) {
          setAssessments(data.data || []);
        } else {
          setError(data.message || "Unable to fetch assessments");
        }
      } catch (err) {
        setError("Error fetching assessments: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [moduleId, userId]);

  const handleAttemptClick = async (assessmentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/getAttempts?assessmentId=${assessmentId}&userId=${userId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch attempt data");
      }

      const data = await response.json();

      if (data.success) {
        const nextAttempt = data.activeAttempt || data.nextAttempt || 1;
        navigate("/attemptpage", {
          state: { assessmentId, attempt: nextAttempt },
        });
      } else {
        throw new Error(data.message || "Unable to fetch attempts");
      }
    } catch (err) {
      console.error("Error fetching attempts:", err);
      alert("Failed to fetch attempt data. Please try again later.");
    }
  };

  const handleViewAttempts = (assessmentId) => {
    navigate("/attempt", { state: { assessmentId } });
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center my-4">
        {error}
      </Alert>
    );
  }

  return (
    <div className="container">
      {assessments.length === 0 ? (
        <p className="text-center text-muted">No assessments available.</p>
      ) : (
        <Row className="justify-content-center">
          {assessments.map((assessment) => (
            <Col
              xs={12}
              lg={12}
              key={assessment.assessment_id}
              className="mb-3"
            >
              <Card className="shadow-sm">
                <Card.Body className="d-flex flex-column flex-lg-row justify-content-between align-items-center">
                  <div className="d-flex align-items-center mb-3 mb-lg-0">
                    <GoCheckCircleFill
                      size={20}
                      style={{ marginRight: "10px", color: "#263F86" }}
                    />
                    <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
                      {assessment.assessment_name || "Unnamed Assessment"}
                    </span>
                  </div>

                  <div className="d-flex flex-column flex-lg-row">
                    {assessment.status === "submit" || assessment.status === "unsubmit"  ? (
                      <>
                        {String(assessment.type || "")
                          .trim()
                          .toLowerCase() !== "exam" && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mb-2 mb-lg-0 me-lg-2 w-100 w-lg-auto"
                            onClick={() =>
                              handleViewAttempts(assessment.assessment_id)
                            }
                          >
                            View My Attempts
                          </Button>
                        )}
                        <Button
                          variant="btn"
                          size="sm"
                          className="w-100 w-lg-auto"
                          style={{background: "#263F86", color: "white"}}
                          onClick={() =>
                            handleAttemptClick(assessment.assessment_id)
                          }
                        >
                          {assessment.status === "unsubmit"
                            ? "Resume"
                            : "Take Again"}
                        </Button>
                      </>
                    ) : (
                      <Button
                        style={{ background: "#263F86" }}
                        size="sm"
                        className="w-100 w-lg-auto"
                        onClick={() =>
                          handleAttemptClick(assessment.assessment_id)
                        }
                      >
                        Start
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

export default LearnerAssessment;
